/**
 * Sprint 17 B1: Visual Journey Canvas — multi-channel, drag-drop reorder
 *
 * Renders journey nodes in a vertical flow layout.
 * Nodes are connected by edges drawn as CSS lines.
 * Supports: selection, add, remove, drag-drop reorder.
 *
 * 10 node types: trigger, message, sms, whatsapp, dm, wait,
 * condition, split, webhook, goal.
 */
import { useState, useCallback, useRef } from "react";
import { useTranslation } from "@/i18n";
import type {
  JourneyNode,
  JourneyEdge,
  JourneyNodeType,
  TriggerNodeData,
  MessageNodeData,
  WaitNodeData,
  ConditionNodeData,
  GoalNodeData,
  SmsNodeData,
  WhatsAppNodeData,
  DmNodeData,
  SplitNodeData,
  WebhookNodeData,
} from "./types";
import { TriggerNode } from "./node-trigger";
import { MessageNode } from "./node-message";
import { WaitNode } from "./node-wait";
import { ConditionNode } from "./node-condition";
import { GoalNode } from "./node-goal";
import { SmsNode, WhatsAppNode, DmNode } from "./node-channel";
import { SplitNode } from "./node-split";
import { WebhookNode } from "./node-webhook";
import { NodePalette } from "./node-palette";

interface JourneyCanvasProps {
  nodes: JourneyNode[];
  edges: JourneyEdge[];
  onNodesChange: (nodes: JourneyNode[]) => void;
  onEdgesChange: (edges: JourneyEdge[]) => void;
  /** Optional filter for palette (used by DM flow builder) */
  allowedNodeTypes?: JourneyNodeType[];
}

function generateNodeId(): string {
  return `node_${crypto.randomUUID()}`;
}

function createDefaultNode(type: JourneyNodeType, index: number): JourneyNode {
  const id = generateNodeId();
  const position = { x: 300, y: 100 + index * 120 };
  const label = type;

  switch (type) {
    case "trigger":
      return { id, type: "trigger", position, label, triggerKind: "form_submitted", config: {} } satisfies TriggerNodeData;
    case "message":
      return { id, type: "message", position, label, subject: "", body: "", intent: "general" } satisfies MessageNodeData;
    case "sms":
      return { id, type: "sms", position, label, body: "" } satisfies SmsNodeData;
    case "whatsapp":
      return { id, type: "whatsapp", position, label, body: "" } satisfies WhatsAppNodeData;
    case "dm":
      return { id, type: "dm", position, label, platform: "instagram", body: "" } satisfies DmNodeData;
    case "wait":
      return { id, type: "wait", position, label, duration: 1, unit: "days" } satisfies WaitNodeData;
    case "condition":
      return { id, type: "condition", position, label, field: "", operator: "equals", value: "" } satisfies ConditionNodeData;
    case "split":
      return { id, type: "split", position, label, variants: [{ id: "a", name: "A", percentage: 50 }, { id: "b", name: "B", percentage: 50 }] } satisfies SplitNodeData;
    case "webhook":
      return { id, type: "webhook", position, label, url: "", method: "POST" } satisfies WebhookNodeData;
    case "goal":
      return { id, type: "goal", position, label, eventType: "booking.created", eventConfig: {}, conversionWindowDays: 7 } satisfies GoalNodeData;
  }
}

function renderNode(
  node: JourneyNode,
  selectedId: string | null,
  onSelect: (id: string) => void,
) {
  const props = { isSelected: selectedId === node.id, onSelect };
  switch (node.type) {
    case "trigger": return <TriggerNode node={node} {...props} />;
    case "message": return <MessageNode node={node} {...props} />;
    case "sms": return <SmsNode node={node} {...props} />;
    case "whatsapp": return <WhatsAppNode node={node} {...props} />;
    case "dm": return <DmNode node={node} {...props} />;
    case "wait": return <WaitNode node={node} {...props} />;
    case "condition": return <ConditionNode node={node} {...props} />;
    case "split": return <SplitNode node={node} {...props} />;
    case "webhook": return <WebhookNode node={node} {...props} />;
    case "goal": return <GoalNode node={node} {...props} />;
  }
}

export function JourneyCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  allowedNodeTypes,
}: JourneyCanvasProps) {
  const { t } = useTranslation();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const handleAddNode = useCallback(
    (type: JourneyNodeType) => {
      const newNode = createDefaultNode(type, nodes.length);
      const updated = [...nodes, newNode];
      onNodesChange(updated);

      // Auto-connect to previous node
      const lastNode = nodes[nodes.length - 1];
      if (lastNode) {
        const newEdge: JourneyEdge = {
          id: `edge_${lastNode.id}_${newNode.id}`,
          source: lastNode.id,
          target: newNode.id,
        };
        onEdgesChange([...edges, newEdge]);
      }
    },
    [nodes, edges, onNodesChange, onEdgesChange],
  );

  const handleRemoveSelected = useCallback(() => {
    if (!selectedNodeId) return;
    const updatedNodes = nodes.filter((n) => n.id !== selectedNodeId);
    const updatedEdges = edges.filter(
      (e) => e.source !== selectedNodeId && e.target !== selectedNodeId,
    );
    onNodesChange(updatedNodes);
    onEdgesChange(updatedEdges);
    setSelectedNodeId(null);
  }, [selectedNodeId, nodes, edges, onNodesChange, onEdgesChange]);

  // Drag-drop reordering
  const handleDragStart = useCallback((index: number) => {
    dragIndexRef.current = index;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback((index: number) => {
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === index) return;
    dragIndexRef.current = null;

    const updated = [...nodes];
    const moved = updated.splice(fromIndex, 1)[0];
    if (!moved) return;
    updated.splice(index, 0, moved);
    onNodesChange(updated);

    // Rebuild edges for linear flow
    const newEdges: JourneyEdge[] = [];
    for (let i = 0; i < updated.length - 1; i++) {
      const current = updated[i];
      const next = updated[i + 1];
      if (current && next) {
        newEdges.push({
          id: `edge_${current.id}_${next.id}`,
          source: current.id,
          target: next.id,
        });
      }
    }
    onEdgesChange(newEdges);
  }, [nodes, onNodesChange, onEdgesChange]);

  return (
    <div className="flex gap-4">
      {/* Palette */}
      <div className="w-52 shrink-0">
        <NodePalette onAddNode={handleAddNode} allowedTypes={allowedNodeTypes} />
        {selectedNodeId && (
          <button
            type="button"
            onClick={handleRemoveSelected}
            className="mt-3 w-full rounded-md bg-[var(--color-error)]/10 px-3 py-2 text-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error)]/20 transition-colors"
          >
            {t("journey.canvas.removeNode")}
          </button>
        )}
      </div>

      {/* Canvas area */}
      <div className="flex-1 relative min-h-[500px] border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)]/50 p-6 overflow-auto">
        {nodes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[var(--color-muted-foreground)] text-sm">
            {t("journey.canvas.empty")}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {nodes.map((node, idx) => (
              <div
                key={node.id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(idx)}
                className="cursor-grab active:cursor-grabbing"
              >
                {renderNode(node, selectedNodeId, setSelectedNodeId)}
                {/* Connector line between nodes */}
                {idx < nodes.length - 1 && (
                  <div className="flex justify-center py-1">
                    <div className="w-px h-8 bg-[var(--color-border)]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
